import { ui } from '@procuros/datachecks';
import isEqual from 'lodash/isEqual';
import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';

import { SuccessfulEnrichment } from 'hooks/useGruAgent/types';
import { DataStores } from 'services/datachecks/types';
import { WebEDIError } from 'services/webediErrors/types';
import { IntegrationProcessDTO, IssueResolverType, ProcessSpecificationDTO, TransactionState } from 'support/types';

type State = {
  uiConfig: Array<ui.Section>;
  initialCanonical: Record<string, any> | undefined;
  isLoading: boolean;
  isValidating: boolean;
  isValidationComplete: boolean;
  isBigMessage: boolean;
  rawErrorCount: number;
  processSpecificationFields: ProcessSpecificationDTO['fields'] | undefined;
  dataStores: DataStores | undefined;
  destinationProcess: IntegrationProcessDTO | undefined | null;
  sourceProcess: IntegrationProcessDTO | undefined | null;
  issueResolver: IssueResolverType | undefined;
  hiddenErrors: Array<WebEDIError>;
  fieldPathsWithAIEditions: Set<string>;
  fieldPathsWithAISuggestions: Set<string>;
  gruSuggestionEnrichment: SuccessfulEnrichment | null;
  articlesToAddToMasterdata: Set<string>;
  messageState: TransactionState | undefined;
};

export const initialState: State = {
  uiConfig: [],
  isLoading: true,
  isValidating: false,
  isValidationComplete: false,
  isBigMessage: false,
  rawErrorCount: 0,
  processSpecificationFields: undefined,
  dataStores: undefined,
  initialCanonical: undefined,
  destinationProcess: undefined,
  sourceProcess: undefined,
  issueResolver: undefined,
  hiddenErrors: [],
  fieldPathsWithAIEditions: new Set(),
  fieldPathsWithAISuggestions: new Set(),
  gruSuggestionEnrichment: null,
  articlesToAddToMasterdata: new Set(),
  messageState: undefined,
};

export const useWebEDIStore = create(
  immer<State>(() => ({
    ...initialState,
  })),
);

export const setIsValidating = (isValidating: boolean) => useWebEDIStore.setState({ isValidating });
export const setIsValidationComplete = (isValidationComplete: boolean) =>
  useWebEDIStore.setState({ isValidationComplete });
export const setIsBigMessage = (isBigMessage: boolean) => useWebEDIStore.setState({ isBigMessage });
export const setRawErrorCount = (rawErrorCount: number) => useWebEDIStore.setState({ rawErrorCount });
export const setInitialCanonical = (initialCanonical: Record<string, any> | undefined) =>
  useWebEDIStore.setState({ initialCanonical });
export const setUiConfig = (uiConfig: Array<ui.Section>) =>
  useWebEDIStore.setState((state) => {
    if (isEqual(state.uiConfig, uiConfig)) {
      return state;
    }
    return { uiConfig };
  });
export const setIsLoading = (isLoading: boolean) => useWebEDIStore.setState({ isLoading });
export const setProcessSpecificationFields = (
  processSpecificationFields: ProcessSpecificationDTO['fields'] | undefined,
) =>
  useWebEDIStore.setState((state) => {
    if (isEqual(state.processSpecificationFields, processSpecificationFields)) {
      return state;
    }
    return { processSpecificationFields: processSpecificationFields };
  });

export const setDataStores = (dataStores: DataStores | undefined): void => {
  useWebEDIStore.setState((state) => {
    if (isEqual(state.dataStores, dataStores)) {
      return state;
    }
    return { dataStores };
  });
};

export const setDestinationProcess = (destinationProcess: IntegrationProcessDTO | undefined | null) =>
  useWebEDIStore.setState({ destinationProcess });
export const setSourceProcess = (sourceProcess: IntegrationProcessDTO | undefined | null) =>
  useWebEDIStore.setState({ sourceProcess });
export const setIssueResolver = (issueResolver: IssueResolverType | undefined) =>
  useWebEDIStore.setState({ issueResolver });
export const setHiddenErrors = (hiddenErrors: Array<WebEDIError>) =>
  useWebEDIStore.setState((state) => {
    if (isEqual(state.hiddenErrors, hiddenErrors)) {
      return state;
    }
    return { hiddenErrors };
  });
export const setFieldPathsWithAIEditions = (fieldPathsWithAIEditions: Set<string>) => {
  const current = useWebEDIStore.getState().fieldPathsWithAIEditions;
  if (
    current.size === fieldPathsWithAIEditions.size &&
    Array.from(current).every((path) => fieldPathsWithAIEditions.has(path))
  ) {
    return;
  }
  useWebEDIStore.setState({ fieldPathsWithAIEditions });
};
export const setFieldPathsWithAISuggestions = (fieldPathsWithAISuggestions: Set<string>) => {
  const current = useWebEDIStore.getState().fieldPathsWithAISuggestions;
  if (
    current.size === fieldPathsWithAISuggestions.size &&
    Array.from(current).every((path) => fieldPathsWithAISuggestions.has(path))
  ) {
    return;
  }
  useWebEDIStore.setState({ fieldPathsWithAISuggestions });
};
export const setGruSuggestionEnrichment = (gruSuggestionEnrichment: SuccessfulEnrichment | null) =>
  useWebEDIStore.setState({ gruSuggestionEnrichment });
export const toggleArticleToAddToMasterdata = (annotationId: string) => {
  const current = useWebEDIStore.getState().articlesToAddToMasterdata;
  const next = new Set(current);
  if (next.has(annotationId)) {
    next.delete(annotationId);
  } else {
    next.add(annotationId);
  }
  useWebEDIStore.setState({ articlesToAddToMasterdata: next });
};

export const setMessageState = (messageState: TransactionState | undefined) =>
  useWebEDIStore.setState({ messageState });

export const cleanStore = () => useWebEDIStore.setState(initialState);
